import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { getApiClient, overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { ClusterResponse, ClusterStatus } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { enhanceCluster } from './enhanceCluster';
import { getClusterQueryKey } from './useClusterQuery';

export const EXTERNAL_CLUSTERS_QUERY_KEY = 'external-clusters';

export const getClustersQueryKey = (
  params: {
    targetOrgId?: string;
    withDiscovered?: boolean;
    skipDemo?: boolean;
  } = {}
) => {
  if (Object.keys(params).length === 0) {
    return [EXTERNAL_CLUSTERS_QUERY_KEY] as const;
  }

  // withDiscovered only affects client-side select filtering, not the API request,
  // so it must not be part of the cache key to avoid duplicate cache entries.
  return [
    EXTERNAL_CLUSTERS_QUERY_KEY,
    params.targetOrgId,
    params.skipDemo ?? false,
  ] as const;
};

export type UseClustersQueryArgs = {
  polling?: boolean;
  enabled?: boolean;
  withDiscovered?: boolean;
  organizationId?: string;
  skipMiddleware?: boolean;
};

export const getClustersQueryOptions = ({
  targetOrgId,
  withDiscovered,
  skipDemo,
  polling,
  queryClient,
  enabled = true,
}: {
  targetOrgId?: string;
  withDiscovered: boolean;
  skipDemo: boolean;
  polling?: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getClustersQueryKey({ targetOrgId, withDiscovered, skipDemo }),
    queryFn: async () => {
      const apiClient = getApiClient();
      const { data } = await apiClient.clusters.getClusters({
        headers: overrideOrganizationHeader(targetOrgId),
        middlewareArgs: {
          skipMiddleware: [skipDemo && 'demo-middleware'],
        },
      });

      return (
        data.items?.map((cluster) => {
          const enhancedData = enhanceCluster(cluster as ClusterResponse);

          queryClient.setQueryData(
            getClusterQueryKey({ id: enhancedData.id }),
            enhancedData
          );

          return enhancedData;
        }) || []
      );
    },
    refetchInterval: polling ? 20_000 : false,
    enabled: makeIsEnabled(!!targetOrgId, enabled),
    select: (data) =>
      withDiscovered
        ? data
        : data?.filter(
            (cluster) => cluster.status !== ClusterStatus.DISCOVERED
          ),
  });

export const useClustersQuery = ({
  polling,
  enabled = true,
  withDiscovered = false,
  organizationId,
  skipMiddleware = false,
}: UseClustersQueryArgs = {}) => {
  const { currentOrganization: organization } = useOrganizations();
  const queryClient = useQueryClient();

  const targetOrgId = organizationId ?? organization?.id;

  const skipDemo =
    (!!organizationId &&
      !!organization?.id &&
      organizationId !== organization.id) ||
    skipMiddleware;

  const {
    data: clusters,
    error,
    refetch: refetchClusters,
    isLoading,
    isRefetching,
    status,
  } = useQuery(
    getClustersQueryOptions({
      targetOrgId,
      withDiscovered,
      skipDemo,
      polling,
      queryClient,
      enabled,
    })
  );

  return { clusters, error, isLoading, isRefetching, status, refetchClusters };
};
